import { appearDown } from "Pages/Audience/ClassificationsPage";
import { motion } from "motion/react";
import clsx from "clsx";
import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ApiAudience,
  ApiClassification,
  ApiClassificationItem,
  ApiProject,
} from "@incendium/api";
import {
  ArrowBack,
  Delete,
  Edit,
  Reorder,
  Settings,
} from "@mui/icons-material";
import { useConfirmationContext } from "Providers/ConfirmationProvider";
import { useSnackbar } from "notistack";
import { audienceService } from "Apis";
import ClassificationItemDialog from "Components/ClassificationDialog/ClassificationItemDialog";
import { useMemo, useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ColItem,
  ColsTitle,
  useColsLayoutStyles,
} from "Components/ColsLayout/ColsLayout";
import Appear from "Components/Animate/Appear";
import AudienceDialog from "features/audiences/components/AudienceDialog";
import { TypographyHelp } from "Components/UI/TypographyHelp";
import StyledDrawer, {
  StyledDrawerContainer,
  StyledDrawerTitle,
} from "Components/UI/StyledDrawer";
import CopyClipboardBtn from "Components/CopyClipboardBtn/CopyClipboardBtn";
import { traitParam } from "@incendium/inc-ts-helpers";

// todo, type
const ItemRow = ({
  item,
  selectedClassificationItem,
  selectClassificationItem,
  onClassItemEdit,
  onConfig,
  onItemDelete,
  id,
}: any) => {
  const { colHandle } = useColsLayoutStyles({});
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [audience, setAudience] = useState<ApiAudience>({});
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const onAudienceSaved = (a: ApiAudience) => {
    setAudienceOpen(false);
    setAudience(a);
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : undefined,
        background: isDragging ? "#e3f2fd" : undefined,
      }}
      {...attributes}
    >
      <motion.div
        variants={{
          hidden: { opacity: 0 },
          show: { opacity: 1 },
        }}
      >
        {isDragging && <Divider />}
        <ColItem>
          <ListItem
            className={clsx({
              active: item.id === selectedClassificationItem?.id,
            })}
            button
            onClick={() => selectClassificationItem(item)}
          >
            {!selectedClassificationItem && (
              <div className={colHandle} style={{ display: "flex", alignItems: "center", cursor: "grab" }} {...listeners}>
                <ListItemIcon>
                  <Reorder color="secondary" />
                </ListItemIcon>
              </div>
            )}
            <ListItemText
              primary={item.value}
              secondary={item.description || "..."}
              primaryTypographyProps={{
                noWrap: true,
              }}
              secondaryTypographyProps={{
                noWrap: true,
                variant: "caption",
              }}
            />
            {!selectedClassificationItem && (
              <Appear>
                <ListItemSecondaryAction>
                  <IconButton color="secondary" onClick={onConfig} size="large">
                    <Settings />
                  </IconButton>

                  <IconButton
                    color="secondary"
                    onClick={() => onClassItemEdit(item)}
                    size="large"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="secondary"
                    onClick={() => onItemDelete(item)}
                    size="large"
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </Appear>
            )}
          </ListItem>
          <Divider />
        </ColItem>
      </motion.div>
      <AudienceDialog
        open={audienceOpen}
        setOpen={setAudienceOpen}
        audience={audience}
        onSaved={onAudienceSaved}
      />
    </div>
  );
};

function ClassificationItemsCol({
  selectedClassification,
  selectClassificationItem,
  selectedClassificationItem,
  setSelectedClassificationItem,
  selectedProject,
  setSelectedClassification,
  onClassItemSaved,
}: {
  selectedClassification: ApiClassification | null;

  selectClassificationItem: (i: ApiClassificationItem) => void;
  selectedClassificationItem: ApiClassificationItem | null;
  setSelectedClassificationItem: React.Dispatch<
    React.SetStateAction<ApiClassificationItem | null>
  >;
  selectedProject: ApiProject;
  setSelectedClassification: React.Dispatch<
    React.SetStateAction<ApiClassification | null>
  >;
  onClassItemSaved: (classificationItem: ApiClassificationItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [classificationItem, setClassificationItem] =
    useState<ApiClassificationItem | null>(null);
  const { openConfirmation, closeConfirmation } = useConfirmationContext();
  const { enqueueSnackbar } = useSnackbar();

  const items = useMemo(() => {
    return (selectedClassification?.classificationItems || [])
      .map((c) => c)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [selectedClassification]);

  const onClassItemEdit = (classificationItem: ApiClassificationItem) => {
    setClassificationItem(classificationItem);
    setOpen(true);
  };

  const onItemDelete = (item: ApiClassificationItem) => {
    openConfirmation({
      title: `Are you sure you want to delete this item`,
      body: `This action can not be undone`,
      callback: async () => {
        await audienceService.audienceServiceDeleteClassificationItem({
          projectId: selectedProject?.id as number,
          classificationId: selectedClassification?.id as number,
          classificationItemId: item.id as number,
        });
        const items = [...(selectedClassification?.classificationItems || [])];
        const idx = items.findIndex((ci) => ci.id === item.id);
        if (idx !== -1) items.splice(idx, 1);

        setSelectedClassification({
          ...selectedClassification,
          classificationItems: items,
        });

        enqueueSnackbar(`${item.value} Deleted`, {
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
        setSelectedClassificationItem(null);

        closeConfirmation();
      },
    });
  };

  const itemIds = items.map((item, i) => String(item.id || i));
  const onReorder = (oldIndex: number, newIndex: number) => {
    const newItems = arrayMove(items, oldIndex, newIndex).map((item, i) => ({
      ...item,
      order: i + 1,
    }));
    setSelectedClassification({
      ...selectedClassification,
      classificationItems: newItems,
    });
  };

  return (
    <div>
      <ColsTitle>
        {selectedClassification ? (
          <>
            <motion.span
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <TypographyHelp
                variant="body1"
                iconSize="small"
                placement="right"
                tooltip={
                  <>
                    <p>
                      A lead can only have one value per trait; for example, the
                      trait might be "hair color" and the value "brown". The
                      values are in a cascading order, which means that we check
                      to see if the first one is a match and if it isn't, we try
                      the second rule and so on.
                    </p>
                  </>
                }
                text=" Possible Values"
              />

              <Typography variant="body2" component="span">
                (Trait : {selectedClassification.name})
              </Typography>
            </motion.span>
            {!selectedClassificationItem ? (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpen(true)}
              >
                Add New Value
              </Button>
            ) : (
              <Tooltip title="Back To Possible Values" placement="top">
                <IconButton
                  disabled={!selectedClassificationItem}
                  onClick={() => {
                    setSelectedClassificationItem(null);
                  }}
                  size="large"
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>
            )}
          </>
        ) : (
          <Typography variant="body1" color="textSecondary">
            Possible Values
          </Typography>
        )}
      </ColsTitle>
      {selectedClassification &&
        selectedClassification.classificationItems &&
        selectedClassification.classificationItems.length > 0 && (
          <motion.div initial="hidden" animate="show" variants={appearDown}>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={event => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                const oldIndex = itemIds.indexOf(active.id as string);
                const newIndex = itemIds.indexOf(over.id as string);
                if (oldIndex === -1 || newIndex === -1) return;
                onReorder(oldIndex, newIndex);
              }}
            >
              <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                <List disablePadding>
                  {items.map((item, idx) => (
                    <ItemRow
                      key={item.id}
                      id={String(item.id || idx)}
                      item={item}
                      selectedClassificationItem={selectedClassificationItem}
                      selectClassificationItem={selectClassificationItem}
                      onClassItemEdit={onClassItemEdit}
                      onConfig={() => setConfigOpen(!configOpen)}
                      onItemDelete={onItemDelete}
                    />
                  ))}
                </List>
              </SortableContext>
            </DndContext>
          </motion.div>
        )}
      {open && (
        <ClassificationItemDialog
          onClose={() => {
            setSelectedClassificationItem(null);
            setOpen(false);
          }}
          classification={selectedClassification}
          classificationItem={classificationItem}
          project={selectedProject}
          onSaved={(classificationItem) => {
            setOpen(false);
            onClassItemSaved(classificationItem);
          }}
        />
      )}
      <StyledDrawer open={configOpen} onClose={() => setConfigOpen(false)}>
        <StyledDrawerTitle>
          <Typography variant="subtitle1"> Manually Set Trait</Typography>
          <Typography variant="body2">
            Use one of these options to manually set a leads trait.
          </Typography>
        </StyledDrawerTitle>
        {selectedClassificationItem && (
          <>
            <StyledDrawerContainer>
              <Typography variant="body1">
                <strong>Key</strong> : {selectedClassificationItem.param}
                <CopyClipboardBtn
                  color="secondary"
                  text={`${selectedClassificationItem.param}`}
                  stopPropagation
                  isIconButton
                  sx={{ marginLeft: 1 }}
                />
              </Typography>
              <Typography variant="body1">
                <strong>URL Search Param</strong> :{" "}
                {`${traitParam}=${selectedClassificationItem.param}`}
                <CopyClipboardBtn
                  color="secondary"
                  text={`${selectedClassificationItem}=${selectedClassificationItem.param}`}
                  stopPropagation
                  isIconButton
                  sx={{ marginLeft: 1 }}
                />
              </Typography>
              <Typography variant="body1">
                <strong>JS Function</strong> :{" "}
                <i>inc("add_trait", "{selectedClassificationItem.param}");</i>
                <CopyClipboardBtn
                  color="secondary"
                  text={`inc("add_trait", "${selectedClassificationItem.param}");`}
                  stopPropagation
                  isIconButton
                  sx={{ marginLeft: 1 }}
                />
              </Typography>
            </StyledDrawerContainer>
          </>
        )}
      </StyledDrawer>
    </div>
  );
}

export default ClassificationItemsCol;
